import { getPlayersSearch } from "@aura-frontend/omnibus-az-client";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import {
  Button,
  cn,
  Icons,
  Input,
  Label,
  useDebounce,
} from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { CreatePlayersModal } from "@/components/CreatePlayersModal";
import { PlayersTable } from "@/components/PlayersTable/PlayersTable";
import UploadNewPlayersModal from "@/components/upload-new-players-modal";
import { useManageLayoutContext } from "@/pages/Manage/ManageLayout";

const playersInfiniteQueryOptions = (
  visibilityGroup: string,
  search?: string,
) =>
  infiniteQueryOptions({
    queryKey: ["getPlayersSearch", visibilityGroup, search],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getPlayersSearch({
        query: {
          visibilityGroup,
          continue: pageParam,
          ...(search && { name: search }),
        },
        signal,
        throwOnError: true,
      });

      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.continue,
    enabled: !!visibilityGroup,
  });

const SearchPlayersPage = () => {
  const { orgId } = useManageLayoutContext();
  const [isUploadingNewPlayersModalOpen, setUploadingNewPlayersModalOpen] =
    useState(false);
  const [isCreatePlayersModalOpen, setCreatePlayersModalOpen] = useState(false);
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [rawSearchValue, setSearchValue] = useState("");
  const searchValue = useDebounce(rawSearchValue, 500, false);

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery(playersInfiniteQueryOptions(orgId ?? "", searchValue));

  const players = useMemo(
    () => data?.pages.flatMap((page) => page.players ?? []) ?? [],
    [data?.pages],
  );

  return (
    <div className="relative flex flex-col">
      <div
        className={cn(
          "flex flex-col-reverse gap-8 items-start justify-start px-5",

          "md:flex-row md:items-end md:gap-4 md:justify-between",
        )}
      >
        <div>
          <Label htmlFor="operator-search" className="mb-2 text-sm">
            Search
          </Label>

          <Input
            placeholder="Look for a player"
            className="w-50 lg:w-60"
            name="operator-search"
            id="operator-search"
            inputMode="text"
            type="text"
            value={rawSearchValue}
            isClearable={true}
            onClear={() => {
              setSearchValue("");
            }}
            iconRight={
              <Icons.MagnifyingGlass className="text-placeholder h-5 w-5" />
            }
            onChange={(e) => {
              if (e.target.value === "") {
                setSearchValue("");
                return;
              }

              setSearchValue(e.target.value);
            }}
          />
        </div>

        <div className="flex gap-2 lg:gap-4">
          <Button
            size="sm"
            variant="secondary-flat-light-outline"
            onClick={() => setUploadingNewPlayersModalOpen(true)}
          >
            Bulk Upload
          </Button>

          <Button
            onClick={() => setCreatePlayersModalOpen(true)}
            variant="secondary-flat-light"
            size="sm"
          >
            <Icons.Plus className="size-4" />
            Add Players
          </Button>
        </div>
      </div>

      <UploadNewPlayersModal
        orgId={orgId!}
        open={isUploadingNewPlayersModalOpen}
        onOpenChange={setUploadingNewPlayersModalOpen}
        onSubmit={() => {
          refetch();
        }}
      />

      <CreatePlayersModal
        orgId={orgId!}
        open={isCreatePlayersModalOpen}
        onOpenChange={setCreatePlayersModalOpen}
        onPlayersCreated={() => {
          refetch();
        }}
      />

      <div className="mt-8">
        <PlayersTable
          data={players}
          shouldRenderActions={true}
          pageSize={100}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isLoading || isFetchingNextPage}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          showJerseyNumber={true}
        />
      </div>
    </div>
  );
};

export default SearchPlayersPage;
